// AppShellHost — the composition root: the ONE impure shell, now a thin
// composition root. It calls `useAppShellHost` (which owns all state, effects,
// sub-hooks and handlers and assembles the `AppShellModel`), gates on the boot
// facts, and otherwise renders the provider + the pure view tree.
//
// Everything the render tree needs travels through the AppShellModel context
// (docs/implement/ui-as-projection.md): ChromeShellFrame + AppShellOverlays and
// their children read facts + emit intents off it, so no view closes over a
// host local. Frame state lives in one `useReducer(foldFrame, …)` inside the
// hook — the pure frame decider is the only writer of frame factors.

import { parseSignInError, signInErrorParam } from "@bilda/app/signInError";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useState } from "react";
import { SignInHost } from "../components/Iam/SignIn/SignInHost.js";
import { PublicLanding } from "../components/PublicLanding/PublicLanding.js";
import { Spinner } from "../components/Spinner/Spinner.js";
import {
    AppShellOverlays,
    CoachOverlay,
} from "./AppShellHost/AppShellOverlays.js";
import { ChromeShellFrame } from "./AppShellHost/chrome/ChromeShellFrame.js";
import { FeedbackHost } from "./AppShellHost/chrome/FeedbackHost.js";
import { AppShellProvider } from "./AppShellHost/context.js";
import { BootFailure, NotFoundGate } from "./AppShellHost/gates.js";
import { LiveLibraryProvider } from "./AppShellHost/liveLibrary.js";
import { PrivacyGateHost } from "./AppShellHost/PrivacyGateHost.js";
import { centeredStyle } from "./AppShellHost/styles.js";
import { useAppShellHost } from "./AppShellHost/useAppShellHost.js";
import { buildInfo, versionWithBuild } from "./buildInfo.js";
import { CopyProvider, type CopyRequest, copyTextOf } from "./copy.js";
import { browserOf, osOf } from "./feedback.js";

export const AppShellHost = (): ReactNode => {
    const { t, i18n } = useLingui();
    const model = useAppShellHost();
    const {
        authStatus,
        library,
        libraryError,
        health,
        retryNow,
        ports,
        routeNotFound,
        onRouteHome,
        showNotice,
    } = model;

    // The clipboard door for the whole tree (app/copy.ts): format the text,
    // write it through the port, confirm with a toast. Error details carry the
    // time of the copy (UTC, ISO 8601: unambiguous for whoever reads the
    // report); the clock is read here, in the shell.
    const copy = (request: CopyRequest) => {
        const { text, label } = copyTextOf(
            i18n,
            request,
            versionWithBuild(buildInfo),
            new Date().toISOString(),
        );
        ports
            .copyText(text)
            .then((outcome) =>
                showNotice(
                    outcome === "copied"
                        ? { type: "copied", label }
                        : { type: "copy-failed" },
                ),
            );
    };

    // What a feedback report says about the build and the browser: read once
    // here, in the shell (the user agent is a device read).
    const [feedbackBuild] = useState(() => ({
        version: versionWithBuild(buildInfo),
        browser: browserOf(navigator.userAgent),
        os: osOf(navigator.userAgent),
    }));

    // Which pre-auth door an anonymous visitor is on. View-ephemeral (never
    // survives a reload, carries no domain meaning) — the sign-in gate below
    // is the only reader/writer. Resetting to "landing" on reload is fine:
    // the visitor simply sees the front door again.
    // A failed sign-in lands back here as `?signin_error=<kind>` (FR-1.4):
    // parsed once at this boundary into the closed union, and the visitor goes
    // straight to the sign-in surface that explains it.
    const [signInFailure] = useState(() =>
        parseSignInError(
            new URLSearchParams(window.location.search).get(signInErrorParam),
        ),
    );
    const [preAuthView, setPreAuthView] = useState<"landing" | "signIn">(
        signInFailure === undefined ? "landing" : "signIn",
    );

    // === Pre-auth gate ========================================================
    // An `anonymous` projection means no valid session — never the app (and
    // never a request storm against a guarded `/state`). `authenticated` /
    // `unavailable` (which also covers fs-mode/local, where the auth probe
    // never surfaces `anonymous` — see useLibraryLoad.ts) fall through to the
    // app unchanged. A genuine auth-mode anonymous visitor sees the PUBLIC
    // landing FIRST (the front door); its CTA moves to sign-in — PublicLanding
    // → (CTA) → SignInHost → app, the in-SPA "link not embed" handoff (IDD
    // b3df0043).
    if (authStatus?._tag === "anonymous") {
        if (preAuthView === "landing") {
            return (
                <PublicLanding
                    onPrimaryCta={() => setPreAuthView("signIn")}
                    onSignIn={() => setPreAuthView("signIn")}
                />
            );
        }
        return (
            <SignInHost
                onExternalLogin={ports.beginExternalLogin}
                onProviderLogin={ports.beginProviderLogin}
                onBack={() => setPreAuthView("landing")}
                failure={signInFailure}
            />
        );
    }

    // === 404 gate (F48) =======================================================
    // An unrecognized URL is a real "page not found", shown as soon as the seam
    // raises it (no need to wait on the library load — a 404 needs no data). The
    // auth gate above still wins (no app chrome for an anonymous visitor).
    if (routeNotFound) return <NotFoundGate onHome={onRouteHome} />;

    // === Privacy + copyright consent gate ====================================
    // The first thing after sign-in, before any use (owner decision
    // 2026-10-06): the server refuses every data read with 403
    // privacy_terms_not_accepted until THIS person accepted the CURRENT terms
    // (per person, server-enforced); the library load (and the heartbeat,
    // mid-session after a terms bump) surface it here. Accepting reloads, so
    // every boot read the refusal blocked runs again.
    if (libraryError?._tag === "External.PrivacyTermsNotAccepted")
        return (
            <PrivacyGateHost
                ports={ports}
                onAccepted={() => window.location.reload()}
            />
        );

    // === Loading / failure gates for the initial library load ================
    // Full-screen is the LAST resort (audit D1): taken ONLY when there is
    // genuinely nothing to render (library === null). A terminal error that
    // arrives WITH a loaded shell never nukes it — it floats as a keep-shell
    // banner (AppShellOverlays) instead, keeping the workspace usable.
    if (library === null) {
        // Nothing loaded + a terminal error → the ONE legitimate full screen,
        // calm + always recoverable (401 → re-auth; retriable → retry; else
        // reload). Never the raw errorSlot (which dead-ends on ReAuth/None).
        if (libraryError !== null)
            return (
                <CopyProvider value={copy}>
                    <BootFailure error={libraryError} onRetryNow={retryNow} />
                </CopyProvider>
            );
        // Nothing loaded + the server unreachable: the app never disappears
        // (owner principle 2026-10-19). The shell renders with its views'
        // loading placeholders and the menu's connection indicator, while the
        // backoff retry runs; the first good read fills it in.
        if (health === "online")
            return (
                <div style={centeredStyle}>
                    <Spinner label={t`Loading…`} />
                </div>
            );
    }

    // The chrome shell (F31/F35/F40/F5 reconciled) is the ONE shell: it and the
    // overlays sit on top of the shared `model`, never a fork of the app's
    // state. (The former classic-shell URL valve has been removed.)
    return (
        <AppShellProvider value={model}>
            <LiveLibraryProvider value={model.library}>
                <CopyProvider value={copy}>
                    <FeedbackHost build={feedbackBuild}>
                        <CoachOverlay />
                        <ChromeShellFrame />
                        <AppShellOverlays />
                    </FeedbackHost>
                </CopyProvider>
            </LiveLibraryProvider>
        </AppShellProvider>
    );
};
